import React, { useState, useRef, useEffect } from 'react';
import { Send, User, Bot, Server, CheckCircle, XCircle, AlertCircle, Info, Wifi } from 'lucide-react';

const ChatInterface = ({ messages, onSendMessage, onCallTool, tools, connected, onConnect }) => {
  const [inputMessage, setInputMessage] = useState('');
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [inputMessage]);

  const parseCommand = (input) => {
    const text = input.toLowerCase().trim();
    
    // Math operations
    const mathMatch = text.match(/(?:add|calculate|compute|math)\s*(\d+)\s*\+\s*(\d+)/);
    if (mathMatch) {
      return {
        type: 'tool_call',
        tool: 'add',
        args: { a: parseInt(mathMatch[1]), b: parseInt(mathMatch[2]) },
        description: `Adding ${mathMatch[1]} + ${mathMatch[2]}`
      };
    }

    // Echo commands
    const echoMatch = text.match(/(?:echo|say|repeat)\s+(.+)/);
    if (echoMatch) {
      return {
        type: 'tool_call',
        tool: 'echo',
        args: { text: echoMatch[1] },
        description: `Echoing: "${echoMatch[1]}"`
      };
    }

    // Timestamp commands
    if (text.includes('time') || text.includes('timestamp')) {
      return {
        type: 'tool_call',
        tool: 'timestamp',
        args: {},
        description: 'Getting current timestamp'
      };
    }

    return null;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    const command = parseCommand(inputMessage);
    
    if (command && command.type === 'tool_call') {
      onCallTool(command.tool, command.args);
      onSendMessage(`${command.description}`);
    } else {
      onSendMessage(inputMessage);
    }
    
    setInputMessage('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const getMessageIcon = (type) => {
    switch (type) {
      case 'user':
        return <User className="w-4 h-4 text-blue-500" />;
      case 'system':
        return <Bot className="w-4 h-4 text-gray-500" />;
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'warning':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      case 'info':
      default:
        return <Info className="w-4 h-4 text-blue-500" />;
    }
  };

  const getMessageBgColor = (type) => {
    switch (type) {
      case 'user':
        return 'bg-blue-50 border-blue-200';
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'system':
      case 'info':
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const formatData = (data) => {
    if (!data) return null;
    if (typeof data === 'string') {
      return data;
    }
    return JSON.stringify(data, null, 2);
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className={`w-2 h-2 rounded-full ${connected ? "bg-green-400" : "bg-gray-400"}`}
            />
            <h2 className="text-lg font-semibold text-gray-800">MCP Chat</h2>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">
              {connected
                ? 'Connected to PlusE Finance MCP Server'
                : 'Not connected to MCP server'
              }
            </span>
            {!connected && onConnect && (
              <button
                onClick={onConnect}
                className="flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors text-sm"
              >
                <Wifi className="w-4 h-4" />
                Connect
              </button>
            )}
          </div>
        </div>
        <div className="text-sm text-gray-500 mt-1">
          {tools?.length > 0 ? `${tools.length} tools` : '0 tools'}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* {!connected && (
          <div className="text-center py-8">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-md mx-auto">
              <Wifi className="w-12 h-12 text-blue-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Connect to MCP Server
              </h3>
              <p className="text-gray-600 mb-4">
                Connect to the MCP server to start chatting and using tools.
              </p>
              {onConnect && (
                <button
                  onClick={onConnect}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors mx-auto"
                >
                  <Wifi className="w-4 h-4" />
                  Connect Now
                </button>
              )}
            </div>
          </div>
        )} */}

        {messages.map((message) => (
          <div key={message.id} className="flex gap-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
              {getMessageIcon(message.type)}
            </div>
            <div className="flex-1">
              <div className={`p-3 rounded-lg border ${getMessageBgColor(message.type)}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-500 capitalize">
                    {message.type}
                  </span>
                  <span className="text-xs text-gray-400">
                    {message.timestamp}
                  </span>
                </div>
                <div className="text-sm text-gray-800">
                  {message.content}
                </div>
                {message.data && (
                  <div className="mt-2 p-2 bg-white rounded border">
                    <pre className="text-xs text-gray-600 whitespace-pre-wrap">
                      {formatData(message.data)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        {connected ? (
          <div className="flex gap-2">
            <textarea
              ref={textareaRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={connected
                ? "Type your message or command..."
                : "Connect to MCP server to start chatting..."
              }
              disabled={!connected}
              className="flex-1 resize-none border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              rows={1}
              style={{ minHeight: '40px', maxHeight: '120px' }}
            />
            <button
              onClick={handleSubmit}
              disabled={!inputMessage.trim() || !connected}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-gray-500 mb-3">
              Connect to the MCP server to start chatting
            </p>
            {onConnect && (
              <button
                onClick={onConnect}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors mx-auto"
              >
                <Wifi className="w-4 h-4" />
                Connect to Server
              </button>
            )}
          </div>
        )}

        {/* Tool suggestions when connected */}
        {connected && tools && tools.length > 0 && (
          <div className="mt-3">
            <div className="text-xs text-gray-500 mb-2">Available tools:</div>
            <div className="flex flex-wrap gap-2">
              {tools.slice(0, 5).map((tool) => (
                <button
                  key={tool.name}
                  onClick={() => onCallTool(tool.name, {})}
                  className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
                >
                  {tool.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatInterface;